import React, { useContext } from 'react';
import { Box, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { ActiveView } from '../shared/constants';
import { assistantTemplatePrompts, mediaCreationTools, podcastTools } from '../shared/utils';
import AIToolsLink from './AIToolsLink';
import BreadcrumbSeparator from './BreadcrumbSeparator';
import { getCurrentConversation } from '../../../reducers/aiAssistant.selectors';
import { useSelector } from 'react-redux';
import AiAssistantBreadcrumb from './AIAssistantBreadcrumbs';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import useBackToAIToolsMain from '../shared/useBackToAIToolsMain';
import AssistantSectionTopBar from '../../PageToolbar/AssistantSectionTopBar';

const containerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces.xs,
  width: '100%',
  overflow: 'hidden',
};

const getOutlinedIcon = (iconName: IconName): IconName => {
  const iconMapping: Partial<Record<IconName, IconName>> = {
    aiGroupTasks: 'aiGroupTasksOutlined',
    aiLearningObjectiveGeneration: 'aiLearningObjectiveGenerationOutlined',
    aiLessonSeriesPlanning: 'aiLessonSeriesPlanningOutlined',
    aiLessonPlanning: 'aiLessonPlanningOutlined',
    createWorksheet: 'createWorksheetOutlined',
    essayTemplate: 'essayTemplateOutlined',
    explainTopic: 'explainTopicOutlined',
    boldGallery: 'galleryOutlined',
    microphone: 'microphone2',
  };

  return iconMapping[iconName] || iconName;
};

const AIToolsTopBarContent: React.FC = () => {
  const { t } = useTranslation();
  const {
    activeView,
    selectedTemplatePromptId,
  } = useContext(aiAssistantContext);
  const currentConversation = useSelector(getCurrentConversation);
  const handleBackToMain = useBackToAIToolsMain();

  // Determine which breadcrumb to show
  let breadcrumbTitle: string | undefined;
  let breadcrumbIcon: IconName | undefined;

  const shouldShowTemplateBreadcrumb = (
    activeView === ActiveView.TEMPLATE_PROMPT_FORM
    || activeView === ActiveView.PODCAST_LOADING
  ) && selectedTemplatePromptId;

  if (shouldShowTemplateBreadcrumb) {
    let selectedTemplate;
    for (const templates of Object.values(assistantTemplatePrompts)) {
      if (templates) {
        selectedTemplate = templates.find(template => template.id === selectedTemplatePromptId);
        if (selectedTemplate) break;
      }
    }
    if (!selectedTemplate) {
      selectedTemplate = [...mediaCreationTools, ...podcastTools]
        .find(tool => tool.id === selectedTemplatePromptId);
    }
    if (selectedTemplate) {
      breadcrumbTitle = selectedTemplate.title;
      breadcrumbIcon = getOutlinedIcon(selectedTemplate.icon);
    }
  } else if (currentConversation?.data && 'title' in currentConversation.data) {
    breadcrumbTitle = currentConversation.data.title;
    breadcrumbIcon = 'aiChatsOld';
  }

  if (activeView === ActiveView.FREE_DRAFT_ENTRY) {
    return (
      <AssistantSectionTopBar
        data-testid='ai-tools-top-bar-content'
        alwaysRow
        rowSx={{ width: '100%', overflow: 'hidden' }}
        icon={
          <SvgIcon
            component={getMuiIcon('aiTools')}
            sx={{
              color: palette.text.primary,
              width: 24,
              height: 24,
              flexShrink: 0,
            }}
            viewBox='0 0 24 24'
          />
        }
        title={t('KI Tools')}
      />
    );
  }

  return (
    <Box data-testid='ai-tools-top-bar-content' sx={containerStyles}>
      <AIToolsLink onClick={handleBackToMain} isClickable={!!(currentConversation || selectedTemplatePromptId)} />
      {breadcrumbTitle && breadcrumbIcon && (
        <>
          <BreadcrumbSeparator />
          <AiAssistantBreadcrumb
            title={breadcrumbTitle}
            iconName={breadcrumbIcon}
            {...(breadcrumbIcon === 'microphone2' && {
              iconSx: { width: 16, height: 16, '& path': { fill: palette.text.primary } },
              iconViewBox: '0 0 48 48',
            })}
          />
        </>
      )}
    </Box>
  );
};

export default AIToolsTopBarContent;

